import { Instance, SnapshotIn, types } from 'mobx-state-tree';
import { splitTagsAndSegments } from '@stores/content-stores/models/news-and-media-utils';

const AuthorInfoModel = types.model('AuthorInfoModel', {
    name: types.maybeNull(types.string),
    title: types.maybeNull(types.string),
    bio: types.maybeNull(types.string),
    website: types.maybeNull(types.string),
    company: types.maybeNull(types.string),
    contributorsUrl: types.maybeNull(types.string),
    hubspotAvatarUrl: types.maybeNull(types.string),
    xUrl: types.maybeNull(types.string),
    facebookUrl: types.maybeNull(types.string),
    linkedinUrl: types.maybeNull(types.string),
});

const MediaCommentModel = types.model('MediaCommentModel', {
    avatarUrl: types.maybeNull(types.string),
    comment: types.maybeNull(types.string),
    commentId: types.maybeNull(types.string),
    creationDate: types.maybeNull(types.string),
    name: types.maybeNull(types.string),
    parentId: types.maybeNull(types.string),
});

const TableOfContentSubtitleModel = types.model('TableOfContentSubtitleModel', {
    title: types.maybeNull(types.string),
    url: types.maybeNull(types.string),
});

const TableOfContentModel = types.model('TableOfContentModel', {
    title: types.maybeNull(types.string),
    url: types.maybeNull(types.string),
    subtitles: types.array(TableOfContentSubtitleModel),
});

const MediaDetailsMetadataModel = types.model('MediaDetailsMetadataModel', {
    authorInfo: types.maybeNull(AuthorInfoModel),
    comments: types.array(MediaCommentModel),
    commentsCount: types.maybeNull(types.number),
    duration: types.maybeNull(types.string),
    likes: types.maybeNull(types.number),
    locale: types.maybeNull(types.string),
    provider: types.maybeNull(types.string),
    views: types.maybeNull(types.number),
    tableOfContent: types.maybeNull(types.array(TableOfContentModel)),
});

export const MediaDetailsModel = types.snapshotProcessor(
    types.model('MediaDetailsModel', {
        id: types.maybeNull(types.number),
        slug: types.maybeNull(types.string),
        mediaId: types.maybeNull(types.number),
        title: types.maybeNull(types.string),
        content: types.maybeNull(types.string),
        imageUrl: types.maybeNull(types.string),
        mediaUrl: types.maybeNull(types.string),
        isFeatured: types.maybeNull(types.number),
        mediaType: types.maybeNull(types.string),
        mediaTypes: types.array(types.maybeNull(types.string)),
        metadata: types.maybeNull(MediaDetailsMetadataModel),
        authorName: types.maybeNull(types.string),
        authorUrl: types.maybeNull(types.string),
        comments: types.maybeNull(types.number),
        duration: types.maybeNull(types.string),
        likes: types.maybeNull(types.number),
        locale: types.maybeNull(types.string),
        provider: types.maybeNull(types.string),
        videoUrl: types.maybeNull(types.string),
        views: types.maybeNull(types.number),
        position: types.maybeNull(types.string),
        publishedDate: types.maybeNull(types.string),
        summary: types.maybeNull(types.string),
        tags: types.maybeNull(types.array(types.string)),
        segments: types.maybeNull(types.array(types.string)),
        url: types.maybeNull(types.string),
    }),
    {
        preProcessor(sn: any) {
            const [cleanTags, cleanSegments] = splitTagsAndSegments(sn.tags);
            return {
                ...sn,
                mediaId: sn.id,
                tags: cleanTags,
                segments: cleanSegments,
                mediaTypes: [sn.mediaType],
                publishedDate:
                    !sn.publishedDate || sn.publishedDate === '0000-00-00 00:00:00'
                        ? '1970-01-01'
                        : sn.publishedDate,
            };
        },
    }
);

const MediaModel = types.snapshotProcessor(
    types.model('MediaModel', {
        mediaId: types.maybeNull(types.number),
        id: types.maybeNull(types.number),
        title: types.maybeNull(types.string),
        imageUrl: types.maybeNull(types.string),
        url: types.maybeNull(types.string),
        isFeatured: types.maybeNull(types.number),
        mediaType: types.string,
        metadata: types.maybeNull(MediaDetailsMetadataModel),
        position: types.maybeNull(types.string),
        publishedDate: types.maybeNull(types.string),
        summary: types.maybeNull(types.string),
        tags: types.maybeNull(types.array(types.string)),
        content: types.maybeNull(types.string),

        // Calculated
        mediaTypes: types.maybeNull(types.array(types.string)),
        origTags: types.maybeNull(types.array(types.string)),
        segments: types.maybeNull(types.array(types.string)),
    }),
    {
        preProcessor(sn: any) {
            const [cleanTags, cleanSegments] = splitTagsAndSegments(sn.tags);
            return {
                ...sn,
                origTags: sn.tags,
                tags: cleanTags,
                segments: cleanSegments,
                mediaTypes: [sn.mediaType],
                publishedDate:
                    sn.publishedDate === '0000-00-00 00:00:00' ? '1970-01-01' : sn.publishedDate,
            };
        },
    }
);

export const NewsAndMediaModel = types.snapshotProcessor(
    types.model('NewsAndMediaModel', {
        medias: types.map(MediaDetailsModel),
        mediasCount: types.maybeNull(types.number),
    }),
    {
        preProcessor(sn: any) {
            const asMap: Record<string, TypeMediaModel> = {};
            if (Array.isArray(sn.medias)) {
                sn.medias
                    .filter((media: TypeMediaModel) => media.publishedDate)
                    .forEach((m: any) => {
                        const id = String(m.id ?? m.mediaId);
                        asMap[id] = m;
                    });
            }
            console.log('preProcessor :  ', {
                ...sn,
                medias: asMap,
                mediasCount: sn.medias?.length ?? 0,
            });
            return {
                ...sn,
                medias: asMap,
                mediasCount: sn.medias?.length ?? 0,
            };
        },
    }
);
export type TypeMediaModel = Instance<typeof MediaModel>;
export type TypeNewsAndMediaModel = SnapshotIn<typeof MediaModel>;

export default NewsAndMediaModel;
